import { useEffect, useState } from 'react';
import {
  Alert,
  Anchor,
  Badge,
  Box,
  Button,
  Group,
  Loader,
  Modal,
  Stack,
  Table,
  Text,
  Title,
} from '@mantine/core';
import { IconRefresh } from '@tabler/icons-react';
import {
  edoExpeditionsApi,
  type ExpeditionAction,
  type ExpeditionItem,
} from '../../shared/api/edo-expeditions';

const ACTION_LABEL: Record<ExpeditionAction['kind'], string> = {
  ForwarderApproval: 'Принять поручение (согласовать)',
  ForwarderReceiptIssuance: 'Выдать экспедиторскую расписку',
  OrderRevocation: 'Отозвать поручение',
  WarehouseReceiptIssuance: 'Складская расписка',
};

/**
 * Поручения экспедитору в ЭДО.МИГ24 (экспедиции): список входящих/исходящих со
 * статусом, участниками и пакетами; действия (согласование, выдача расписки) —
 * когда мы экспедитор; открытие в кабинете МИГ24.
 */
export function ExpeditionsPage() {
  const [items, setItems] = useState<ExpeditionItem[]>([]);
  const [webBase, setWebBase] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Модалка действий по экспедиции.
  const [actId, setActId] = useState<string | null>(null);
  const [actions, setActions] = useState<ExpeditionAction[] | null>(null);
  const [actBusy, setActBusy] = useState(false);
  const [actMsg, setActMsg] = useState<{ ok: boolean; text: string } | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await edoExpeditionsApi.list();
      setItems(res.items ?? []);
      setWebBase(res.webBase ?? '');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось загрузить экспедиции');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    void load();
  }, []);

  const openActions = async (id: string) => {
    setActId(id);
    setActions(null);
    setActMsg(null);
    try {
      setActions(await edoExpeditionsApi.actions(id));
    } catch (e) {
      setActMsg({ ok: false, text: e instanceof Error ? e.message : 'Не удалось получить действия' });
      setActions([]);
    }
  };
  const runAction = async (kind: ExpeditionAction['kind']) => {
    if (!actId) return;
    setActBusy(true);
    setActMsg(null);
    try {
      const res = await edoExpeditionsApi.performAction(actId, kind);
      if (res.ok && res.signUrl) {
        window.open(res.signUrl, '_blank', 'noopener,noreferrer');
        setActMsg({ ok: true, text: 'Документ создан — открыта страница подписания в ЭДО.МИГ24.' });
        void load();
      } else {
        const errs = res.validation?.errors ?? [];
        setActMsg({
          ok: false,
          text: errs.length ? `Замечания ЭДО: ${errs.join('; ')}` : 'Действие не выполнено.',
        });
      }
    } catch (e) {
      setActMsg({ ok: false, text: e instanceof Error ? e.message : 'Ошибка действия' });
    } finally {
      setActBusy(false);
    }
  };

  return (
    <Box>
      <Group justify="space-between" mb="md">
        <Title order={3}>Поручения ЭДО</Title>
        <Button variant="default" leftSection={<IconRefresh size={16} />} onClick={load} loading={loading}>
          Обновить
        </Button>
      </Group>

      <Text size="sm" c="dimmed" mb="md">
        Экспедиции (поручения экспедитору) из ЭДО.МИГ24 — входящие и исходящие. Когда
        мы экспедитор, доступны действия «Принять поручение» и «Выдать расписку».
        Подписание выполняется в кабинете МИГ24 по открывшейся вкладке.
      </Text>

      {error && (
        <Alert color="red" mb="md" withCloseButton onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Loader />
      ) : items.length === 0 ? (
        <Text c="dimmed">Экспедиций пока нет.</Text>
      ) : (
        <Table striped withTableBorder>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Номер</Table.Th>
              <Table.Th>Статус</Table.Th>
              <Table.Th>Участники</Table.Th>
              <Table.Th>Документы</Table.Th>
              <Table.Th></Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {items.map((e) => (
              <Table.Tr key={e.id}>
                <Table.Td>{e.number ?? e.id.slice(0, 8)}</Table.Td>
                <Table.Td>
                  <Badge variant="light">{e.statusName ?? '—'}</Badge>
                </Table.Td>
                <Table.Td>
                  <Stack gap={2}>
                    {(e.participants ?? []).map((p, i) => (
                      <Text key={i} size="xs">
                        {p.roleName ?? p.roleCode ?? 'участник'}: {p.name ?? p.inn ?? '—'}
                      </Text>
                    ))}
                  </Stack>
                </Table.Td>
                <Table.Td>
                  <Stack gap={2}>
                    {(e.packages ?? []).map((pk, i) => (
                      <Text key={i} size="xs">
                        {pk.name ?? pk.kind ?? 'документ'}
                        {pk.statusName ? ` — ${pk.statusName}` : ''}
                        {pk.isDraft ? ' (черновик)' : ''}
                      </Text>
                    ))}
                  </Stack>
                </Table.Td>
                <Table.Td>
                  <Group gap="xs" wrap="nowrap">
                    <Button size="compact-xs" variant="light" onClick={() => openActions(e.id)}>
                      Действия
                    </Button>
                    {webBase && (
                      <Anchor href={`${webBase}/expeditions/${e.id}`} target="_blank" rel="noopener noreferrer" size="sm">
                        Открыть в ЭДО
                      </Anchor>
                    )}
                  </Group>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      )}

      <Modal opened={actId !== null} onClose={() => setActId(null)} title="Действия по экспедиции" size="md">
        <Stack>
          {actions === null ? (
            <Loader size="sm" />
          ) : actions.length === 0 ? (
            <Text size="sm" c="dimmed">
              Доступных действий нет (возможно, вы не экспедитор по этой экспедиции или
              документ уже в работе).
            </Text>
          ) : (
            <Stack gap="xs">
              {actions.map((a) => (
                <Button
                  key={a.kind}
                  variant="light"
                  loading={actBusy}
                  onClick={() => runAction(a.kind)}
                >
                  {ACTION_LABEL[a.kind] ?? a.kind}
                </Button>
              ))}
            </Stack>
          )}
          {actMsg && (
            <Alert color={actMsg.ok ? 'green' : 'red'}>{actMsg.text}</Alert>
          )}
        </Stack>
      </Modal>
    </Box>
  );
}
